// Pure BullMQ conventions shared by every producer that writes the `bullmq:*`
// Redis namespace: the core-clients queue services and core-effect's
// QueueProducerClient (which a lint boundary keeps from importing
// core-clients directly). All producers must agree on the Redis prefix, the
// jobId sanitization, the finished-job retention windows, and the alive-state
// vocabulary — any drift silently splits cross-producer dedupe and status
// lookups. This module is dependency-free on purpose so both sides can
// import it.
//
// One more shared convention lives at the option-mapping layer of each
// producer rather than here: BullMQ has no per-job `timeout` option, so
// producers store the legacy `timeout` value as a CUSTOM key on the job's
// opts (BullMQ round-trips unknown opts keys through Redis verbatim), and
// the consumer dispatcher reads it back to enforce the timeout. This is the
// canonical description — the mappers/shims just point here.
import * as Time from './time_helper.js';
export const BULLMQ_PREFIX = 'bullmq';
// Deadline applied by the consumer dispatcher when a job carries no timeout
// opts key. Also used to estimate job expiry for status endpoints.
export const DEFAULT_BULLMQ_JOB_TIMEOUT_MS = 5 * Time.MINUTE;
// Grace windows applied when a producer does not explicitly opt into a
// removal policy: completed jobs linger 3 min, failed jobs 1 min, before
// BullMQ's age-based auto-removal reaps them. (Bull relied on a CleanupJob
// with these delays; BullMQ has no CleanupJob, so a false/unset value would
// leak the job in Redis forever.)
export const COMPLETED_JOB_GRACE_SECONDS = 180;
export const FAILED_JOB_GRACE_SECONDS = 60;
// BullMQ per-job states that mean the job is alive and will be processed.
// 'waiting-children' and 'prioritized' are BullMQ-only; 'paused' does not
// exist as a per-job state in BullMQ. Typed as strings so this module needs
// no bullmq dependency — callers pass BullMQ's JobState values.
export const ALIVE_BULLMQ_JOB_STATES = new Set([
    'active',
    'waiting',
    'waiting-children',
    'delayed',
    'prioritized',
]);
// Queue-resident subset of the alive states: jobs in these states may be
// removed and re-added by a higher-priority duplicate. BullMQ parks any job
// with priority > 0 in 'prioritized'; only priority-0 jobs report 'waiting'
// (Bull reported both as 'waiting'). Every job we enqueue carries a nonzero
// Priority, so replacement must accept both.
export const REPLACEABLE_BULLMQ_JOB_STATES = new Set([
    'waiting',
    'prioritized',
]);
// Job-count types (Queue.getJobCountByTypes vocabulary — 'wait', not
// 'waiting') that together mean "queued, not yet picked up". Counting only
// 'wait' undercounts to ~0 because every job we enqueue carries a nonzero
// priority and therefore parks in 'prioritized' — the same state-vocabulary
// trap as REPLACEABLE_BULLMQ_JOB_STATES above.
export const QUEUED_BULLMQ_JOB_COUNT_TYPES = ['wait', 'paused', 'prioritized'];
// Map a BullMQ per-job state onto the legacy vocabulary.
// 'waiting-children'/'prioritized' collapse to 'waiting'; 'unknown' (and any
// state added by a future BullMQ upgrade) maps to 'stuck'.
export function normalizeBullMqJobState(state) {
    switch (state) {
        case 'completed':
        case 'waiting':
        case 'active':
        case 'delayed':
        case 'failed':
        case 'paused':
            return state;
        case 'waiting-children':
        case 'prioritized':
            return 'waiting';
        default:
            return 'stuck';
    }
}
// BullMQ rejects two classes of custom jobIds that Bull tolerated for years
// (https://docs.bullmq.io/guide/jobs/job-ids):
//
// 1. ':' anywhere — its Redis keys are colon-delimited; Job.create throws
//    "Custom Id cannot contain :". Legacy callers (e.g. kor SMS idempotency
//    keys) produced colon-bearing ids and broke at the BullMQ cutover
//    (2026-06-10 incident). Mapped ':' -> '-'.
// 2. Digit-only ids — they collide with BullMQ's auto-generated numeric ids;
//    Job.create throws "Custom Id cannot be integers". Several builders pass
//    bare epoch timestamps as the spec jobId; the spec path survives only
//    because fullJobId is prefixed with the jobType, so this guards the
//    remaining paths. Prefixed with '#' — the workaround the BullMQ docs
//    recommend. Note BullMQ's own check is parseInt-based, so the simpler
//    /^\d+$/ here diverges on exotic shapes (e.g. '0123' is transformed
//    though BullMQ would accept it; '-123' passes though BullMQ rejects it).
//    Accepted trade-off for readability: realistic ids are ObjectIds, epochs,
//    and kebab strings.
//
// Both transforms are deterministic AND idempotent ('#123' no longer matches
// /^\d+$/; '-' never re-maps), which layered application depends on (add
// entry points sanitize, then the option mappers re-apply).
export function sanitizeBullMqJobId(fullJobId) {
    const deColoned = fullJobId.replace(/:/g, '-');
    if (deColoned.match(/^\d+$/)) {
        return `#${deColoned}`;
    }
    return deColoned;
}
// Translate a legacy-shaped removeOnComplete/removeOnFail value into a
// BullMQ-safe one. Honor explicit caller intent (true / keep-last-N number /
// {age,count}); only default false-or-unset to a timed window so job-status
// checks resolve before the job is reaped.
export function normalizeJobRemovalOption(value, defaultAgeSeconds) {
    if (value === true ||
        typeof value === 'number' ||
        (typeof value === 'object' && value !== null)) {
        return value;
    }
    return { age: defaultAgeSeconds };
}
//# sourceMappingURL=bullmq_helper.js.map